import { useState } from 'react';
import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { CheckCheck } from 'lucide-react';

interface MarkAllReadButtonProps extends Omit<HTMLChakraProps<'button'>, 'onClick'> {
  onMarkAll: () => Promise<unknown>;
}

/**
 * "Mark all as read" for the bell panel and the Notifications page. Looking at either
 * one reads nothing, so this is the one way to clear the list without opening every row.
 * Style props override the look, so each surface can match its own buttons.
 */
export function MarkAllReadButton({ onMarkAll, ...rest }: MarkAllReadButtonProps) {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onMarkAll();
    } finally {
      setBusy(false);
    }
  };

  return (
    <chakra.button
      type="button"
      h="40px"
      px="4"
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap="1.5"
      border="none"
      borderRadius="full"
      bg="volyx.lightGray"
      color="volyx.text"
      fontSize="14px"
      fontWeight="semibold"
      whiteSpace="nowrap"
      cursor="pointer"
      transition="background-color 0.15s ease, transform 0.1s ease, opacity 0.15s ease"
      _active={{ transform: 'scale(0.98)' }}
      _disabled={{ opacity: 0.6, cursor: 'default' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'volyx.action', outlineOffset: '2px' }}
      disabled={busy}
      {...rest}
      onClick={() => void handleClick()}
    >
      <CheckCheck size={16} aria-hidden />
      Mark all as read
    </chakra.button>
  );
}
